/**
 * 项目使用 hash 路由，在某些入口场景下 URL 会被错误地添加额外的查询参数，导致出现多个?：
 *    1. app 金刚区入口
 *    2. 短链重定向入口
 *
 * 本工具函数用于统一处理 URL 中的查询参数，既解析路径后的 query，也解析哈希片段中的 query，
 * 并返回一组用于读取或遍历这些合并参数的辅助方法。
 *
 * 例如：`https://foo.com/page?name=bob#/step?token=abc&name=alice`
 * 最终会得到 name=alice（哈希覆盖同名 query）和 token=abc。
 */
import { showToast } from "@/pages/charge-vip/utils";
import { getBrowserEnv } from "@/utils/tools";
import { myInit, wxInit } from "./common";

export const getSearchParams = () => {
  // 获取当前页面的完整URL
  const url = window.location.href;

  // 创建一个Map来存储参数，后出现的会覆盖先出现的
  const paramsMap = new Map<string, string>();

  // 解析URL，提取查询参数和哈希参数
  try {
    // 创建URL对象
    const urlObj = new URL(url);

    // 处理查询参数（?后面的参数）
    urlObj.searchParams.forEach((value, key) => {
      paramsMap.set(key, value);
    });

    // 处理哈希部分（#后面的参数）
    const hash = urlObj.hash;
    if (hash) {
      // 移除#符号
      const hashContent = hash.substring(1);
      const strippedHashContent = hashContent.startsWith("/")
        ? hashContent.slice(1)
        : hashContent;
      let hashQuery = "";

      // 如果有?，从?开始提取；否则尝试把整个hash当作查询串（需包含=），例如
      // #/step?foo=1&bar=2 -> foo=1&bar=2；#foo=1&bar=2 -> foo=1&bar=2
      if (hashContent.includes("?")) {
        hashQuery = hashContent.substring(hashContent.indexOf("?") + 1);
      } else if (strippedHashContent.includes("=")) {
        // 无?但包含键值对时，把整个片段视作查询串（兼容#/path/foo=1&bar=2）
        hashQuery = strippedHashContent;
      }

      // 如果提取到了查询字符串，解析参数
      if (hashQuery) {
        const hashParams = new URLSearchParams(hashQuery);

        // 将哈希中的参数添加到Map中，会覆盖同名的查询参数
        hashParams.forEach((value, key) => {
          paramsMap.set(key, value);
        });
      }
    }
  } catch (error) {
    console.error("Error parsing URL:", error);
  }

  // 返回一个带有get方法的对象
  return {
    get: (key: string): string | null => {
      return paramsMap.get(key) || null;
    },
    // 额外添加一些有用的方法
    has: (key: string): boolean => {
      return paramsMap.has(key);
    },
    getAll: (): Record<string, string> => {
      const result: Record<string, string> = {};
      paramsMap.forEach((value, key) => {
        result[key] = value;
      });
      return result;
    },
  };
};

/**
 * 从 URL 的 hash 片段或 search 片段中提取查询参数。
 * 优先从 hash 中提取，如果 hash 中不存在，则从 search 中提取。
 *
 * 兼容 hash 内嵌套的多段 query，包括以下情况：
 * - `#/path?foo=1?bar=2` 会被规范成 `foo=1&bar=2`
 * - `#/path?foo=1?bar=2?baz=3` 会被规范成 `foo=1&bar=2&baz=3`
 * - `#/path?foo=1&bar=2?baz=3` 会被规范成 `foo=1&bar=2&baz=3`
 * - `#/path?foo=1???bar=2` 会被规范成 `foo=1&&&bar=2`
 *
 * 处理逻辑：
 * 1. 优先解析 hash 中的 query：找到第一个?的位置，将后续内容中所有的?替换为&，使用 URLSearchParams 解析
 * 2. 如果 hash 中未找到指定参数，则解析 window.location.search
 *
 * 常见示例：
 * `https://foo.com?id=123#/step?token=abc`
 * `getQueryHashString("token")` -> `abc` (from hash)
 * `getQueryHashString("id")` -> `123` (from search)
 */
export const getQueryHashString = (name: string) => {
  const hash = window.location.hash ?? "";
  const queryIndex = hash.indexOf("?");

  if (queryIndex !== -1) {
    // Replace secondary "?" with "&" so we can treat all pairs uniformly
    const normalized = hash.slice(queryIndex + 1).replace(/\?/g, "&");
    const params = new URLSearchParams(normalized);
    const val = params.get(name);
    if (val !== null) return val;
  }

  // Fallback to search params
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(name);
};

/**
 * 由于当前充电H5的路由是hash模式，所有的query参数都在hash中，所以需要从hash中获取参数，否则在小程序端会获取不到
 */
export const getParamsFromHash = (key: string) => {
  const value = getQueryHashString(key);

  // 如果未找到参数或参数值为 undefined/null，返回空字符串
  if (!value || ["undefined", "null"].includes(value)) {
    return "";
  }

  return decodeURIComponent(value);
};

export const getParamsFromHref = (key: string) => {
  const params = {};
  const href = window.location.href;
  const queryString = href.split("?")[1];
  if (queryString) {
    queryString.split("&").forEach((item) => {
      const [key, value] = item.split("=");
      params[key] = value;
    });
  }
  if (!params[key] || ["undefined", "null"].includes(params[key])) {
    return "";
  }
  // 避免路由hash模式时，获取到的query参数带有#/
  if (params[key].indexOf("#/") !== -1) {
    return decodeURIComponent(params[key]).replace("#/", "");
  }
  return decodeURIComponent(params[key]);
};

export const getTopicPageUrl = (pageCode: string) => {
  return `${window.location.origin}/apps/charge-web/par-marketing.html#configPage?pageCode=${pageCode}`;
};

/**
 * 判断是否为问卷星链接
 */
export const isWjxUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    const hostname = new URL(url).hostname;
    return (
      hostname.endsWith("wjx.cn") ||
      hostname === "wjx.cn" ||
      hostname === "survey.xiaopeng.com"
    );
  } catch {
    return false;
  }
};

/**
 * 给问卷星链接追加 sojumpparm 参数（携带 uid）
 * 仅图片组件使用，用于支持单人填写限制
 */
export const appendWjxSojumpparm = (url: string, uid: string): string => {
  if (!uid || !isWjxUrl(url)) return url;
  return appendParamsToUrl(url, { sojumpparm: uid });
};

// 给URL追加参数
export const appendParamsToUrl = (
  url: string,
  params: Record<string, string>,
) => {
  // 如果 URL 包含 hash（#/），则参数需要拼到 hash 后面，而不是 search 部分
  const hashIndex = url.indexOf("#");
  if (hashIndex !== -1) {
    const base = url.slice(0, hashIndex);
    const hash = url.slice(hashIndex); // 包含 #

    // 如果 hash 里已有 ?，直接追加 &；否则先加 ?
    const separator = hash.includes("?") ? "&" : "?";
    const queryStr = new URLSearchParams(params).toString();
    return `${base}${hash}${separator}${queryStr}`;
  }

  // 无 hash 时，按普通 URL 处理，拼到 search 部分
  const urlObj = new URL(url);
  const searchParams = urlObj.searchParams;
  for (const [key, value] of Object.entries(params)) {
    searchParams.set(key, value);
  }
  return urlObj.toString();
};

/**
 * 从 URL 中移除一个或多个指定的查询参数。
 *
 * @param {string} url - 原始的 URL 字符串。
 * @param {string|string[]} paramNames - 要移除的查询参数名称，可以是单个字符串或字符串数组。
 * @returns {string} - 移除了指定参数后的新 URL 字符串。
 */
export function removeUrlParameters(url, paramNames, removeAll = false) {
  if (typeof url !== "string") {
    throw new Error("url 必须是一个字符串");
  }

  try {
    const urlObject = new URL(url);
    console.log("urlObject", urlObject);

    // 1. 优先处理"移除所有参数"（同时清空查询参数和哈希参数）
    if (removeAll) {
      urlObject.search = ""; // 清空 ? 后的查询参数
      const hashSplit = urlObject.hash.split("?")[0]; // 保留 # 基础路径，清空后面的参数
      urlObject.hash = hashSplit;
      return urlObject.href;
    }

    // 2. 处理指定参数（同时处理查询参数和哈希参数）
    if (paramNames !== undefined && paramNames !== null) {
      const paramsToRemove = Array.isArray(paramNames)
        ? paramNames
        : [paramNames];

      // 处理 ? 后的查询参数
      paramsToRemove.forEach((param) => urlObject.searchParams.delete(param));

      // 处理 # 后的片段参数
      if (urlObject.hash.includes("?")) {
        const [hashPath, hashQuery] = urlObject.hash.split("?");
        // 解析哈希参数
        const hashParams = new URLSearchParams(hashQuery);
        paramsToRemove.forEach((param) => hashParams.delete(param));
        // 重组哈希部分
        const newHashQuery = hashParams.toString()
          ? `?${hashParams.toString()}`
          : "";
        urlObject.hash = `${hashPath}${newHashQuery}`;
      }
    }

    return urlObject.href;
  } catch (error) {
    console.error("无效的 URL:", error);
    return url;
  }
}

/**
 * 跳转三方小程序（微信/支付宝）
 * - 小程序环境：跳转到中转页，由原生页面处理
 * - 非小程序环境：使用 URL Scheme 直接跳转
 * @param config 小程序配置
 * @param config.appId 微信小程序 AppID
 * @param config.miniProgramPath 微信小程序页面路径
 * @param config.aliAppId 支付宝小程序 AppID
 * @param config.aliProgramPath 支付宝小程序页面路径
 */
export const jumpToThirdWxMiniProgram = (config: {
  appId?: string;
  miniProgramPath?: string;
  aliAppId?: string;
  aliProgramPath?: string;
}) => {
  const { appId, miniProgramPath, aliAppId, aliProgramPath } = config;
  const { isWXApp, isMyApp } = getBrowserEnv();

  // 根据平台选择对应的 AppID 和路径
  const targetAppId = isMyApp ? aliAppId : appId;
  const targetPath = isMyApp ? aliProgramPath : miniProgramPath;

  // 验证参数
  if (!targetAppId || !targetPath) {
    console.error("小程序配置不完整", { isWXApp, isMyApp, config });
    // 支付宝环境未配置时使用需求指定的提示文案
    if (isMyApp && (!aliAppId || !aliProgramPath)) {
      showToast("当前平台暂不支持打开，敬请谅解~");
    } else {
      showToast("小程序配置不完整");
    }
    return;
  }

  // 小程序环境：跳转到中转页
  if (isWXApp || isMyApp) {
    // 构造中转页 URL 参数
    const redirectParams = new URLSearchParams();
    redirectParams.append("appId", targetAppId);
    redirectParams.append("path", targetPath);
    const redirectUrl = `/pages/redirect-miniprogram/index?${redirectParams.toString()}`;

    if (isWXApp) {
      // 微信小程序环境：跳转到中转页
      wxInit()
        .then((wx) => {
          wx.miniProgram.navigateTo({
            url: redirectUrl,
            success: () => {
              console.log("跳转到微信小程序中转页成功", {
                appId: targetAppId,
                path: targetPath,
              });
            },
            fail: (err: unknown) => {
              console.error("跳转到微信小程序中转页失败", err);
              showToast("跳转小程序失败");
            },
          });
        })
        .catch((err) => {
          console.error("微信 JSSDK 初始化失败", err);
          showToast("跳转小程序失败");
        });
    } else if (isMyApp) {
      // 支付宝小程序环境：跳转到中转页
      myInit()
        .then((my) => {
          my.navigateTo({
            url: redirectUrl,
            success: () => {
              console.log("跳转到支付宝小程序中转页成功", {
                appId: targetAppId,
                path: targetPath,
              });
            },
            fail: (err: unknown) => {
              console.error("跳转到支付宝小程序中转页失败", err);
              showToast("跳转小程序失败");
            },
          });
        })
        .catch((err) => {
          console.error("支付宝 JSSDK 初始化失败", err);
          showToast("跳转小程序失败");
        });
    }
  } else {
    // 非小程序环境：统一使用微信小程序 URL Scheme 跳转
    // 格式：weixin://dl/business/?appid=xxx&path=xxx&query=xxx
    const wxAppId = appId;
    const wxPath = miniProgramPath;

    if (!wxAppId || !wxPath) {
      console.error("微信小程序配置不完整", { appId, miniProgramPath });
      showToast("暂不支持此链接跳转");
      return;
    }

    // 拆分 path 和 query，分别传递
    const [path, query] = wxPath.split("?");
    let scheme = `weixin://dl/business/?appid=${wxAppId}&path=${path}`;
    if (query) {
      scheme += `&query=${encodeURIComponent(query)}`;
    }

    window.location.href = scheme;
    console.log("使用 URL Scheme 跳转微信小程序:", scheme);
  }
};
